import { HandlerTypingReveal } from "./handler-typing-reveal";
import { ArrowUp, Mic, Square } from "lucide-react";
import {
  Heart,
  Users,
  UserRound,
  BriefcaseBusiness,
  House,
  UserRoundPlus,
  CircleHelp,
  Smile,
  CalendarHeart,
  HandHeart,
  Puzzle,
  Sun,
  MessagesSquare,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useVoiceAnswer } from "./use-voice-answer";
import "./handler-starting-clues.css";

const relationships = [
  "My partner",
  "Family",
  "A friend",
  "Someone at work",
  "A neighbor",
  "Someone else",
  "Nobody comes to mind yet",
];
const relationshipIcons = [
  Heart,
  Users,
  UserRound,
  BriefcaseBusiness,
  House,
  UserRoundPlus,
  CircleHelp,
];
const topics = [
  "They love a laugh",
  "A big day is coming",
  "They deserve some thanks",
  "They love a hobby or project",
  "They could use a lift",
  "We should reconnect",
  "I'm not sure yet",
];
const topicIcons = [
  Smile,
  CalendarHeart,
  HandHeart,
  Puzzle,
  Sun,
  MessagesSquare,
  CircleHelp,
];
const places = [
  "At home",
  "At work",
  "Out and about",
  "By message or phone",
  "Not sure yet",
];

type CluesDraft = { page: number; choices: string[]; details: string[] };

function loadCluesDraft(key: string): CluesDraft | null {
  try {
    const raw = localStorage.getItem(key);
    const value = raw ? JSON.parse(raw) : null;
    if (
      !value ||
      !Number.isInteger(value.page) ||
      value.page < 0 ||
      value.page > 2 ||
      !Array.isArray(value.choices) ||
      value.choices.length !== 3 ||
      !value.choices.every((item: unknown) => typeof item === "string") ||
      !Array.isArray(value.details) ||
      value.details.length !== 2 ||
      !value.details.every((item: unknown) => typeof item === "string")
    )
      return null;
    return value;
  } catch {
    return null;
  }
}

function saveCluesDraft(key: string, draft: CluesDraft) {
  try {
    localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    /* storage unavailable */
  }
}

export function HandlerStartingClues({
  name,
  onComplete,
  onRecord,
  storageKey,
}: {
  name: string;
  onComplete: (text: string) => void;
  onRecord?: (prompt: string, text?: string) => void;
  storageKey?: string;
}) {
  const [restored] = useState(() =>
    storageKey ? loadCluesDraft(storageKey) : null,
  );
  const [page, setPage] = useState(() => restored?.page ?? 0);
  const [inputMode, setInputMode] = useState<"voice" | "text">("voice");
  const [choices, setChoices] = useState(
    () => restored?.choices ?? ["", "", ""],
  );
  const [details, setDetails] = useState(() => restored?.details ?? ["", ""]);
  const updateDetail = (text: string) =>
    setDetails((current) =>
      current.map((value, index) => (index === page ? text : value)),
    );
  const voice = useVoiceAnswer(updateDetail);
  const options = [relationships, topics, places][page];
  const questions = [
    "Who's someone you've spent time with lately—or expect to see soon?",
    choices[0] === "Nobody comes to mind yet"
      ? "What catches your interest?"
      : "What might make their day?",
    "Where will you probably cross paths?",
  ];
  // Restored prompts are already saved or retained in the pending queue.
  const recordedQuestion = useRef(restored ? restored.page : -1);
  useEffect(() => {
    if (recordedQuestion.current === page) return;
    recordedQuestion.current = page;
    onRecord?.(
      page === 0
        ? "person"
        : page === 2
          ? "place"
          : choices[0] === "Nobody comes to mind yet"
            ? "interest"
            : "day",
    );
  }, [page, choices, onRecord]);
  useEffect(() => {
    if (storageKey) saveCluesDraft(storageKey, { page, choices, details });
  }, [storageKey, page, choices, details]);
  const next = (answers = choices) => {
    if (page < 2 && details[page].trim()) onRecord?.("", details[page].trim());
    if (page < 2) {
      setInputMode("voice");
      setPage(page + 1);
      return;
    }
    // These are reviewed member selections, sent through the normal chat transport.
    // Keep uncertainty explicit rather than turning it into a claimed personal fact.
    onComplete(
      [
        "Here’s a little about my day:",
        `A person: ${answers[0]}.`,
        ...(details[0].trim()
          ? [`Their name or nickname: ${details[0].trim()}`]
          : []),
        `A starting point: ${answers[1]}.`,
        ...(details[1].trim() ? [`In my words: ${details[1].trim()}`] : []),
        `Where we might cross paths: ${answers[2]}.`,
      ].join("\n"),
    );
  };
  const detailQuestion = (index: number) =>
    index === 0
      ? "What’s their name? A first name or nickname is fine."
      : "Want to tell me a little more about that?";
  const bubble = (text: string, member = false) => (
    <div className="handler-message-row" data-member={member}>
      <div className="handler-bubble" data-member={member}>
        {!member && <small>{name}</small>}
        <p>{text}</p>
      </div>
    </div>
  );
  return (
    <div className="handler-starting-clues">
      <div className="handler-guided-history">
        {questions.slice(0, page).map((question, index) => (
          <div key={index} className="handler-guided-turn">
            {bubble(question)}
            {bubble(choices[index], true)}
            {choices[index] !== "Nobody comes to mind yet" &&
              details[index]?.trim() && (
                <>
                  {bubble(detailQuestion(index))}
                  {bubble(details[index].trim(), true)}
                </>
              )}
          </div>
        ))}
      </div>
      <HandlerTypingReveal
        key={`question-${page}`}
        name={name}
        delay={page === 0 ? 0 : 1200}
      >
        <div className="handler-picker-card">
          <p className="handler-clue-progress">
            Getting to know you · {page + 1} of 3
          </p>
          <h2>{questions[page]}</h2>
          <div
            className="handler-clue-options"
            role="group"
            aria-label={questions[page]}
          >
            {options.map((option, index) => (
              <button
                key={option}
                className={
                  page === 0 && index === relationships.length - 1
                    ? "handler-picker-fallback"
                    : undefined
                }
                type="button"
                aria-pressed={choices[page] === option}
                disabled={voice.busy}
                onClick={() => {
                  const answers = choices.map((value, index) =>
                    index === page ? option : value,
                  );
                  setChoices(answers);
                  onRecord?.(page === 2 ? "finish" : "", option);
                  if (page < 2 && option !== "Nobody comes to mind yet")
                    onRecord?.(page === 0 ? "name" : "detail");
                  if (page === 2) next(answers);
                  else if (
                    page === 0 &&
                    option === "Nobody comes to mind yet"
                  ) {
                    setInputMode("voice");
                    setPage(1);
                  }
                }}
              >
                {((page === 0 && index < relationships.length - 1) ||
                  page === 1) &&
                  (() => {
                    const Icon = (page === 0 ? relationshipIcons : topicIcons)[
                      index
                    ];
                    return <Icon size={20} aria-hidden="true" />;
                  })()}
                <span>{option}</span>
              </button>
            ))}
          </div>
        </div>
      </HandlerTypingReveal>
      {choices[page] && bubble(choices[page], true)}
      {page < 2 &&
        choices[page] &&
        choices[page] !== "Nobody comes to mind yet" && (
          <HandlerTypingReveal
            key={`detail-${page}-${choices[page]}`}
            name={name}
          >
            <div className="handler-clue-detail">
              <div className="handler-message-row">
                <div className="handler-bubble">
                  <small>{name}</small>
                  <p>
                    {page === 0
                      ? "What’s their name? A first name or nickname is fine."
                      : "Want to tell me a little more about that?"}
                  </p>
                </div>
              </div>
              <label className="sr-only" htmlFor="handler-clue-detail">
                {page === 0
                  ? "First name or nickname (optional)"
                  : "A little more about that (optional)"}
              </label>
              {(inputMode === "text" || !!details[page]) && (
                <form
                  className="handler-picker-compose"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (details[page]?.trim() && !voice.busy) next();
                  }}
                >
                  <textarea
                    id="handler-clue-detail"
                    placeholder={
                      page === 0
                        ? "Their name or nickname (optional)"
                        : "Your answer (optional)"
                    }
                    value={details[page]}
                    maxLength={page === 0 ? 50 : 2000}
                    disabled={voice.busy}
                    onChange={(event) => updateDetail(event.target.value)}
                    rows={1}
                  />
                  {!!details[page]?.trim() && (
                    <button
                      type="submit"
                      className="handler-picker-send"
                      aria-label="Send answer"
                      title="Send answer"
                      disabled={voice.busy}
                    >
                      <ArrowUp size={21} strokeWidth={2.5} aria-hidden="true" />
                    </button>
                  )}
                </form>
              )}
              {inputMode === "voice" && (
                <button
                  className="handler-picker-mic"
                  type="button"
                  disabled={voice.busy && voice.status !== "recording"}
                  onClick={() =>
                    voice.status === "recording"
                      ? voice.stop()
                      : void voice.start()
                  }
                >
                  {voice.status === "recording" ? (
                    <Square size={20} aria-hidden="true" />
                  ) : (
                    <Mic size={24} aria-hidden="true" />
                  )}
                  <span>
                    {voice.status === "recording"
                      ? "Stop recording"
                      : voice.busy
                        ? "Processing…"
                        : "Tap to speak"}
                  </span>
                </button>
              )}
              <div className="handler-clue-detail-actions">
                <button
                  className="handler-quiet handler-picker-toggle"
                  type="button"
                  disabled={voice.busy}
                  onClick={() =>
                    setInputMode(inputMode === "voice" ? "text" : "voice")
                  }
                >
                  {inputMode === "voice" ? "Can’t talk" : "Use voice"}
                </button>
                {!details[page]?.trim() && (
                  <button
                    className="handler-quiet handler-picker-toggle"
                    type="button"
                    disabled={voice.busy}
                    onClick={() => next()}
                  >
                    {page === 0
                      ? "Continue without a name"
                      : "Continue without details"}
                  </button>
                )}
              </div>
              {voice.error && (
                <p role="alert">
                  {voice.error} You can type your answer instead.
                </p>
              )}
            </div>
          </HandlerTypingReveal>
        )}
      {page > 0 && (
        <button
          className="handler-quiet"
          type="button"
          disabled={voice.busy}
          onClick={() => setPage(page - 1)}
        >
          Back
        </button>
      )}
    </div>
  );
}
